/**
 * Lexical editor state helpers for @mention insertion and deletion ergonomics,
 * kept out of the plugin component so they can be unit-tested against a headless
 * `createEditor`. Mirrors the reference-text-input behavior for the body editor.
 */
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  LexicalNode,
} from 'lexical';

import { $createMentionNode, $isMentionNode } from './mention-node';

/**
 * Replace the typed `@query` node with a mention token, then insert a trailing
 * plain space and place the caret after it. (spec #14)
 */
export function $insertMention(
  nodeToReplace: LexicalNode | null,
  id: string,
  name: string,
  refType = 'user'
): void {
  // Without a node to anchor to, the mention would never be attached to the
  // document, so inserting the trailing space would throw. Match the plugin's
  // prior behavior and no-op for a null match.
  if (!nodeToReplace) return;
  const mentionNode = $createMentionNode(id, name, refType);
  nodeToReplace.replace(mentionNode);
  const space = $createTextNode(' ');
  mentionNode.insertAfter(space);
  // Caret after the single space character.
  space.select(1, 1);
}

/**
 * Backspacing the plain space immediately after a mention removes both the space
 * and the whole token in one stroke, preserving any text that followed the
 * space. Returns true when it handled the keystroke (so the caller prevents the
 * default single-character delete). (spec #18)
 */
export function $handleMentionBackspace(): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

  const { anchor } = selection;
  // The caret must sit immediately after a leading space in a text node...
  if (anchor.type !== 'text' || anchor.offset !== 1) return false;
  const node = anchor.getNode();
  if (!$isTextNode(node) || node.getTextContent()[0] !== ' ') return false;
  // ...and that text node must directly follow a mention token.
  const mention = node.getPreviousSibling();
  if (mention === null || !$isMentionNode(mention)) return false;

  const remaining = node.getTextContent().slice(1);
  const before = mention.getPreviousSibling();
  const parent = mention.getParent();
  mention.remove();

  if (remaining.length > 0) {
    const replacement = $createTextNode(remaining);
    node.replace(replacement);
    replacement.select(0, 0);
  } else {
    node.remove();
    if (before) {
      before.selectEnd();
    } else if (parent) {
      parent.selectStart();
    }
  }
  return true;
}
